/**
 * selectSlaForSku — typed redux read seam for the Delivery SLA UI.
 *
 * Strictly scoped to the requested SKU. The v2 response can be keyed by a code
 * other than the one requested, but the reducer settles both keys on SUCCESS, so
 * there is no need to scan the pincode for a populated entry — doing that would
 * hand back another size's / product's SLA (stale cards mid-fetch, and cards for
 * a product with no size selected).
 */
import { RawSlaOption } from './types';

/**
 * One array for every "no options yet" read. A fresh `[]` per call is a new
 * identity on each store notification, which breaks `useSelector`'s reference
 * check and re-renders the card forever on a store with no scopedView entry.
 */
const EMPTY_OPTIONS: RawSlaOption[] = [];

interface SlaSliceEntry {
  options?: RawSlaOption[];
  isFetching?: boolean;
  error?: boolean;
  /** B2C: sellers exist beyond the ones in `options`. */
  hasMoreSellers?: boolean;
}

const getEntry = (state: any, pincode?: string, skuId?: string): SlaSliceEntry | undefined => {
  if (!pincode || !skuId) return undefined;
  return state?.edd?.slaDetails?.[pincode]?.[skuId];
};

/** Raw SLA option array for the given pincode + SKU (empty when absent). */
export const getSlaOptionsForSku = (
  state: any,
  pincode?: string,
  skuId?: string
): RawSlaOption[] => getEntry(state, pincode, skuId)?.options || EMPTY_OPTIONS;

export const getIsSlaFetching = (state: any, pincode?: string, skuId?: string): boolean => {
  const entry = getEntry(state, pincode, skuId);
  if (entry && typeof entry.isFetching === 'boolean') return entry.isFetching;
  return Boolean(state?.edd?.isSlaFetching);
};

export const getSlaError = (state: any, pincode?: string, skuId?: string): boolean =>
  Boolean(getEntry(state, pincode, skuId)?.error);

/**
 * True once scopedView has settled for this pincode + SKU — success or error.
 * `options` alone can't say that: an empty array is both "no entry yet" and "no
 * sellers returned", which is why analytics waits on this instead.
 */
export const getIsSlaSettled = (state: any, pincode?: string, skuId?: string): boolean => {
  const entry = getEntry(state, pincode, skuId);
  return Boolean(entry) && entry?.isFetching !== true;
};

/**
 * Active scopedView option for the current size+pincode.
 * When set, offers / EMI should come from this option instead of aggregator variant.
 * Null when no pincode / scopedView not loaded → keep aggregator variant path.
 */
export const getSelectedSlaOption = (state: any): RawSlaOption | null =>
  state?.edd?.selectedSlaOption || null;

/** B2C: `hasMoreSellers` on the scopedView response for this pincode + SKU. */
export const getHasMoreSellers = (state: any, pincode?: string, skuId?: string): boolean =>
  Boolean(getEntry(state, pincode, skuId)?.hasMoreSellers);

/* ---- B2C seller list (scopedView with fetchAllSellers=true) ---- */

const getSellerListEntry = (
  state: any,
  pincode?: string,
  skuId?: string
): SlaSliceEntry | undefined => {
  if (!pincode || !skuId) return undefined;
  return state?.edd?.sellerListSla?.[pincode]?.[skuId];
};

export const getSellerListOptions = (
  state: any,
  pincode?: string,
  skuId?: string
): RawSlaOption[] => getSellerListEntry(state, pincode, skuId)?.options || EMPTY_OPTIONS;

export const getIsSellerListFetching = (
  state: any,
  pincode?: string,
  skuId?: string
): boolean => Boolean(getSellerListEntry(state, pincode, skuId)?.isFetching);

export const getSellerListError = (state: any, pincode?: string, skuId?: string): boolean =>
  Boolean(getSellerListEntry(state, pincode, skuId)?.error);
